import { ColumnAggregationFunctions } from "@multiweb/aggregationRow";
import { AggregationFunction } from "@multiweb/clients/dataReader";
import { getDataReaderClient } from "@multiweb/clients/util";
import { EmptyMessageCustomization } from "@multiweb/frames";
import { ISectionLoggerFactory, LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { HasMasterFrame } from "@multiweb/reactConnectors/mixins/hasMasterFrame";
import { IconCode, IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { UUID } from "Global/Base/IDs/UUID";
import { ROLELESS_ROLE_ID } from "Global/Base/Rights/constants";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { OWObjectList } from "OW.Remoting/Remoting/DataTypes/OWObjectList";
import { IReactFrameConnector } from "UI/UI.ReactControlConnectors/types";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { AdditionalOrdering, ConditionParams, IDataSource, IOrderBy, OrderType } from "../../../../../Global/Base/DataSources/Interfaces";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancellationToken, CancellationTokenSource } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { EMPTY_ITEM_RIGHTS_COLLECTION, IItemRights } from "../../../../../Global/Base/Rights/RightTypes";
import { SecurityUtils } from "../../../../../Global/Base/Security/SecurityUtils";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { ConditionOperator, StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { EOWNoImplicitFormForClassException } from "../../../../../OW.Remoting/Basic/OWExceptions";
import { ApplicationStorage, AppStorageItemType } from "../../../../../OW.Remoting/Remoting/ApplicationStorage";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { BasicDataType } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { ConditionStore } from "../../../../../OW.Remoting/Remoting/ConditionStore";
import { IOWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { NullOWObject, OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { ExportManager, SupportedExportFormats, SupportedReportFormats } from "../../../../../OW.Remoting/Remoting/ExportManager";
import { IWorkflowMenuItem } from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IExtendedObjectOperation, IImplicitFormInfo, ITableExportAttrDesc, ITableExportParams, OWExtendedObjectOperationsType, SHOW_AGGREGATION_ROW_STRING } from "../../../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { PasswordManager } from "../../../../../OW.Remoting/Remoting/PasswordManager";
import { EMPTY_REQUEST_CONTEXT, RequestContext } from "../../../../../OW.Remoting/Remoting/RequestContext";
import { IEvaluatedConditionResult, IRightConditionItem } from "../../../../../OW.Remoting/Remoting/Rights";
import { IBooleanDetailValueEvent } from "../../../../UI.BaseControls/IEvent";
import { Action } from "../../../../UI.Controls/Actions/Action";
import { ActionWithData } from "../../../../UI.Controls/Actions/ActionWithData";
import { ActionContext, ActionType, IAction, IActionContext, IGraphDataForAction, IWFAction, SelectionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { Column } from "../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory, ICalendarColumnDefinition, IColumnDefinition } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { DateColumn } from "../../../../UI.Controls/DataGridSubcontrols/Columns/DateColumn";
import { DateTimeColumn } from "../../../../UI.Controls/DataGridSubcontrols/Columns/DateTimeColumn";
import { FlagColumn } from "../../../../UI.Controls/DataGridSubcontrols/Columns/FlagColumn";
import { TimeColumn } from "../../../../UI.Controls/DataGridSubcontrols/Columns/TimeColumn";
import { IListDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { SeparatorMenuItemWithText } from "../../../../UI.Controls/DropDownMenu/SeparatorItemWithText";
import { IListDataGrid } from "../../../../UI.Controls/IDataGrid";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { loadOriginalRightsDefinition } from "../../../Rights/OriginalRightsDefinitionLoader";
import { convertContextFromFrameToRequest } from "../../../Utilities/SharedUIMethods";
import { Control } from "../../Controls/Control";
import { ICompositesControl, IGenericControl, IHasFrameContext } from "../../Controls/ControlInterfaces";
import { IFRMParams } from "../../Controls/FormParameters";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { PasswordDialogBuilder, PasswordDialogBuilderType } from "../../DataBuilders/PasswordDialogBuilder";
import { RecordsInfoDialogBuilder } from "../../DataBuilders/RecordsInfoDialogBuilder";
import { IChartFrameFormBuilder } from "../../FormBuilders/IFormBuilder";
import { FormManager } from "../../FormManager/IFormManager";
import { Icon, IMenu } from "../../FormUI/FormUI";
import { StandardActionDefinitionBase } from "../../FormUI/FormUIShared";
import { MessageButtonType, MessageType, MessageWindow } from "../../MessageWindow";
import { MessageButtonResult, UIManager } from "../../UIManager";
import { FrameContext } from "../../UIManagerInterfaces";
import { BatchDownloadAction } from "../Actions/BatchDownloadAction";
import { OpenModeType } from "../Actions/DetailFormAction";
import { NodeAction } from "../Actions/NodeAction";
import { StandardAction } from "../Actions/StandardAction";
import { StandardRecordAction } from "../Actions/StandardRecordAction";
import { StandardTwoStateAction } from "../Actions/StandardTwoStateAction";
import { WFAction } from "../Actions/WFAction";
import { WFMultiAction } from "../Actions/WFMultiAction";
import { IControlInfoObject } from "../ControlInfo";
import { DropDownMenuFilterItem } from "../Filters/DropDownMenuItems/DropDownMenuFilterItem";
import { IChartProfileInfo } from "../Forms/IFormProfileInfo";
import { HideButtons } from "../Frames";
import { FormLayout, FrameRefreshType, IForm, IFormSavingParameters } from "../IForm";
import { IAggregableListFrame, IFrame, IFrameWithRights, IListFrame } from "../IFrame";
import { ModalWindowType } from "../ModalWindow";
import { BooleanColumn } from "./ColumnTemplates/BooleanColumn";
import { EnumColumn } from "./ColumnTemplates/EnumColumn";
import { FileColumn } from "./ColumnTemplates/FileColumn";
import { LinkColumn } from "./ColumnTemplates/LinkColumn";
import { LovColumn } from "./ColumnTemplates/LOVColumn";
import { NumberColumn } from "./ColumnTemplates/NumColumn";
import { TextLovColumn } from "./ColumnTemplates/TextLOVColumn";
import {
    IFrameWithProfile,
    IFulltextSearchableFrame,
    IMasterableFrame,
    IMasterableFrameWithDataGrid,
} from "./FrameInterfaces";
import { IBaseFrame } from "./IBaseFrame";
import { IDetailFrame, IDetailMasterableFrame, ISeparateDetailFrame } from "./IDetailMasterableFrame";

const logger = LazyLoadingSectionLogger.create(LoggingSection.frames);

export class FrameSharedMethods {
    private static _columnTypesRegistered: boolean = false;

    private static _pendingEncDialogPromises: Map<string, Promise<boolean>> = new Map<string, Promise<boolean>>(); //Udržuje pole promisů, které zobrazují dialog zadání hesla pro objekt. V případě že by se sešlo více žádostí o zadání hesla pro jeden objekt ve stejnou chvíli, dostanou všechny objekty stejný promis místo toho aby každý otevřel svůj dialog.
    private static _actionDefinitions = new Map<ActionType, StandardActionDefinitionBase>([
        [ActionType.standardBack, {}],
        [ActionType.standardPreviousRecord, {}],
        [ActionType.standardNextRecord, {}],
        [ActionType.standardRefreshList, { context: ActionContext.frameAction }],
        [ActionType.standardNewRecord, { context: ActionContext.frameAction }],
        [ActionType.standardCopyRecord, {}],
        [ActionType.standardEditRecord, {}],
        [ActionType.standardEditRecords, { context: ActionContext.frameAction }],
        [ActionType.standardDeleteRecord, {}],
        [ActionType.standardDeleteRecords, { context: ActionContext.frameAction }],
        [ActionType.standardSaveAll, { context: ActionContext.frameAction }],
        [ActionType.standardCancelChanges, { context: ActionContext.frameAction }],
        [ActionType.standardDecrypt, {}],
        [ActionType.standardMoveRightRecords, { context: ActionContext.frameAction }],
        [ActionType.standardMoveLeftRecords, { context: ActionContext.frameAction }],
        [ActionType.standardPrintAll, { context: ActionContext.frameAction }],
        [ActionType.standardPrintRecord, {}],
        [ActionType.standardExportCSV, { context: ActionContext.frameAction }],
        [ActionType.standardExportExcel, { context: ActionContext.frameAction }],
        [ActionType.standardDownloadAll, { context: ActionContext.frameAction }],
        [ActionType.standardGraph, { context: ActionContext.frameAction }],
        [ActionType.standardColumnSettings, { context: ActionContext.frameAction }],
        [ActionType.standardRecordsCount, { context: ActionContext.frameAction }],
        [ActionType.standardToggleAggregationRow, { context: ActionContext.frameAction }],
        [ActionType.standardSaveRecordInEditContext, { context: ActionContext.frameAction }],
        [ActionType.standardShowSubRecords, { context: ActionContext.frameAction }],

        // parametrický formulář
        [ActionType.standardOpenConditionForm, {}],
        [ActionType.standardCloseAndOpenConditionForm, {}],
        [ActionType.standardOpenResultForm, {}],
        [ActionType.standardRunMethod, {}],
        [ActionType.standardRunMethodAndClose, {}],
        [ActionType.standardCloseAfterWF, {}],

        // MSLOV
        [ActionType.standardSelect, { context: ActionContext.frameAction }],
        [ActionType.standardSelectAll, { context: ActionContext.frameAction }],
        [ActionType.standardDeselectAll, { context: ActionContext.frameAction }],
        [ActionType.standardShowAll, { context: ActionContext.frameAction }],
        [ActionType.standardShowFiltered, { context: ActionContext.frameAction }],

        // kontextové akce
        [ActionType.standardRefreshRecord, {}],
        [ActionType.standardSaveRecord, { context: ActionContext.objectAction }],
        [ActionType.standardSelectRecord, {}],
        [ActionType.standardRowActions, {}],
        [ActionType.standardOpenDetail, {}],
        [ActionType.standardMoveRightRecord, {}],
        [ActionType.standardMoveLeftRecord, {}],
        [ActionType.standardCancelRecordChanges, { context: ActionContext.objectAction }],
        [ActionType.standardOpenDetailOnMainForm, { context: ActionContext.objectAction }],
        [ActionType.standardAuditInfoRecord, { context: ActionContext.objectAction }],
        [ActionType.standardShowWFHistory, { context: ActionContext.objectAction }],
        [ActionType.standardShowWFParticipants, { context: ActionContext.objectAction }],
        [ActionType.standardShowWFStatus, { context: ActionContext.objectAction }],
        [ActionType.standardInfoRecord, { context: ActionContext.objectAction }],

        // akce z definice formuláře
        [ActionType.form, {}],
        [ActionType.parametricForm, {}],
        [ActionType.insert, {}],
        [ActionType.method, {}],
        [ActionType.node, {}],
        [ActionType.report, {}],

        // calendar frame
        [ActionType.standardSwitchIntervalDay, { context: ActionContext.frameAction }],
        [ActionType.standardSwitchIntervalWeek, { context: ActionContext.frameAction }],
        [ActionType.standardSwitchIntervalMonth, { context: ActionContext.frameAction }],
        //[ActionType.standardSwitchIntervalYear, { context: ActionContext.frameAction }],
        [ActionType.standardSetToday, { context: ActionContext.frameAction }],
        [ActionType.standardNextInterval, { context: ActionContext.frameAction }],
        [ActionType.standardPreviousInterval, { context: ActionContext.frameAction }],

        // chart frame
        [ActionType.standardChartName, { context: ActionContext.frameAction }],
        [ActionType.standardMoreSettings, { context: ActionContext.frameAction }],
        [ActionType.standardChartSettings, { context: ActionContext.frameAction }],

        // filter
        [ActionType.standardFilter, { context: ActionContext.frameAction }],
        [ActionType.standardFilterList, { context: ActionContext.frameAction }],
    ]);

    /**
     * Funkce pro kombinaci podmínek
     * @param originalCondition
     * @param right
     * @returns {}
     */
    public static combineRightCondition(originalCondition: string, right: IItemRights): string {
        const addedCondition = ConditionStore.getCondition(right.conditionID);
        const c = right.isNegative ? `not(${addedCondition})` : addedCondition; // `
        return StringUtils.combineConditions(ConditionOperator.and, originalCondition, c);
    }

    /**
     * Rozparsuje element s definicí jednoho sloupce a nastaví hodnoty do datového interface
     * @param column - sloupec, pro který má dojít k parsování atributů
     * @param element - obsahující data ke zpracování
     */
    private static _parseStandardColumnAttributes(column: IColumnDefinition, element: Element): IColumnDefinition {
        const compactArea = element.getAttribute("data-compactArea");
        const compactAreaOrder = parseInt(element.getAttribute("data-compactAreaOrder"));
        const showAttrs = element.getAttribute("data-showAttrs");

        column.dataType = element.getAttribute("data-tscitem");
        column.attrName = element.getAttribute("data-attrName");
        column.id = element.getAttribute("id");
        column.displayFormat = element.getAttribute("data-displayFormat");
        column.locKey = element.getAttribute("data-locKey");
        column.culture = element.getAttribute("data-culture");
        column.visible = DataConverter.toBoolean(element.getAttribute("data-visible"), true);
        column.showAttrs = showAttrs ? showAttrs.split(";") : null;
        column.orderBy = element.getAttribute("data-orderBy");
        column.compactArea = compactArea ? compactArea.toLowerCase() : null;
        column.isEditable = DataConverter.toBoolean(element.getAttribute("data-isEditable"));
        column.width = DataConverter.toNumber(element.getAttribute("data-width"));
        column.compactAreaOrder = (!isNaN(compactAreaOrder)) ? compactAreaOrder : -1;
        column.fileMask = element.getAttribute("data-fileMask");
        column.lovStyle = element.getAttribute("data-lovStyle");
        column.validationClassName = element.getAttribute("data-validationClassName");
        column.editWindow = element.getAttribute("data-editWindow");
        column.showOnlyDetail = DataConverter.toBoolean(element.getAttribute("data-showOnlyDetail"));
        column.condition = element.getAttribute("data-condition");
        column.conditionParams = element.getAttribute("data-conditionParams");
        column.enumName = element.getAttribute("enumname");
        column.allowsignwithoutedit = element.getAttribute("data-allowsignwithoutedit");
        column.allowCounterSignature = element.getAttribute("data-AllowCounterSignature");
        column.showAttr = element.getAttribute("data-showAttr");
        column.additionalAttrs = element.getAttribute("data-additionalAttrs");//additionalAttrs ? additionalAttrs.split(";") : null;
        column.showAdditionalAttrs = element.getAttribute("data-showAdditionalAttrs");//showAdditionalAttrs ? showAdditionalAttrs.split(";") : null;
        column.isIcon = DataConverter.toBoolean(element.getAttribute("data-IsIcon"));
        column.multiline = DataConverter.toBoolean(element.getAttribute("data-multiline"));
        column.minValue = DataConverter.toNumberOrUndefined(element.getAttribute("data-minvalue"));
        column.maxValue = DataConverter.toNumberOrUndefined(element.getAttribute("data-maxvalue"));
        column.originalRightsDefinition = (element instanceof HTMLElement) ? loadOriginalRightsDefinition(element) : EMPTY_ITEM_RIGHTS_COLLECTION;
        column.trimValue = DataConverter.toBoolean(element.getAttribute("data-trimvalue"), true);

        return column;
    }

    /**
     * Funkce přečte pro daný element framu seznam všech atributu použitých pro definování sloupců a vrátí strukturu obsahujici tyto informace
     * @param element html element framu
     * @param selector volitelný selektor, podle kterého mají být vyhledány definice sloupců
     * @returns Vrátí seznam sloupců, které mají být vytvořeny
     */
    public static getColumnsDefinition(element: HTMLElement, selector: string = "[data-behaviour='column']"): IColumnDefinition[] {
        const attributes = Array.from(element.querySelectorAll(selector));
        const columns: IColumnDefinition[] = [];

        for (const attr of attributes) {
            const captionfld = attr.getAttribute("data-captionfld");

            // Ověření vychází z předpokladu, že tento atribut není nikdy prázdný
            if (captionfld) { // Jedná se o typ sloupce pro kalendář, je třeba vytvořit ICalendarColumnDefinition
                const textFields = attr.getAttribute("data-textFields");
                const column: ICalendarColumnDefinition = {
                    captionfld: captionfld,
                    objectName: attr.getAttribute("data-objectName"),
                    dateFromfld: attr.getAttribute("data-datefromfld"),
                    dateTofld: attr.getAttribute("data-datetofld"),
                    itemColor: attr.getAttribute("data-itemColor"),
                    includeTime: DataConverter.toBoolean(attr.getAttribute("data-includeTime")),
                    showFormId: attr.getAttribute("data-showFormId"),
                    canInsert: DataConverter.toBoolean(attr.getAttribute("data-canInsert"), true),
                    textFields: textFields ? textFields.split(";") : null,
                } as ICalendarColumnDefinition;

                columns.push(this._parseStandardColumnAttributes(column, attr));
            } else {
                // Ostatní typy sloupců
                const column: IColumnDefinition = {} as IColumnDefinition;
                columns.push(this._parseStandardColumnAttributes(column, attr));
            }
        }

        return columns;
    }

    /**
    * Zobrazí dialog pro zadání hesla.
    * Zobrazený dialog se uloží jako promis do pole. V případě, že další volající požádá o stejný dialog pro stejný objekt, je mu vrácen promis s již otevřeným dialogem.
    * @param actualObject Aktuální objekt.
    * @param encrypt Atribut jestli dialog zobrazit pro zašifrování nebo dešifrování.
    */
    public static async showEncryptDecryptDialog(actualObject: OWObject, encrypt: boolean): Promise<boolean> {
        const oid = actualObject.instanceID ? actualObject.instanceID : actualObject.id;
        const dialogId = `${actualObject.classInfo.className}-${oid}-${encrypt ? 1 : 0}`;//` unikátní id dialogu, klíč pod kterým bude promis evidován

        if (!FrameSharedMethods._pendingEncDialogPromises.has(dialogId)) {
            const prm = FrameSharedMethods._showEncryptDecryptDialog(actualObject, encrypt);
            FrameSharedMethods._pendingEncDialogPromises.set(dialogId, prm);
            prm.then(_i => FrameSharedMethods._removeEncDialogPromise(dialogId))
                .catch(_i => FrameSharedMethods._removeEncDialogPromise(dialogId));
            return prm;
        } else {
            return FrameSharedMethods._pendingEncDialogPromises.get(dialogId);
        }
    }

    private static _removeEncDialogPromise(uid: string): void {
        FrameSharedMethods._pendingEncDialogPromises.delete(uid);
    }

    private static async _showEncryptDecryptDialog(actualObject: OWObject, encrypt: boolean): Promise<boolean> {
        const msg = new MessageWindow(undefined, ["dialog-encryption"]);
        let action: (password: string) => Promise<boolean>;
        let closeAction: () => Promise<boolean> = null;
        const mainObject = await FrameSharedMethods.findMainObject(actualObject);

        if (encrypt)
            encrypt = !mainObject.isClientSideEncrypted;

        if (!encrypt) {
            action = async (password: string): Promise<boolean> => { // verify password action
                if (StringUtils.isNullOrEmpty(password))
                    return false;

                const cryptoKey = await SecurityUtils.passwordToDeriveKey(password, (await actualObject.getValue("ObjectID")).valueOf()); //await SecurityUtils.passwordToCryptoKey(password);

                if (mainObject.isNew
                )
                    PasswordManager.addTempPassword(mainObject.instanceID, cryptoKey,
                        true); // poslední parametr vyruší vyvolávání události o novém hesle (jinak ho začnou hned používat všichni konzumenti)
                else
                    PasswordManager.addPassword(mainObject.objectName, mainObject.id, cryptoKey, true);

                const decrypt_result = await mainObject.decrypt();
                if (!decrypt_result) //bylo zadáno špatné heslo, musí být odstraněno, aby si pswd manager nepamatoval špatné heslo k objektu.
                    PasswordManager.deletePassword(mainObject.objectName, mainObject.id);
                else {
                    // při úspěchu se heslo nasetuje znovu, tentokrát už s vyvoláním události o novém hesle
                    if (mainObject.isNew)
                        PasswordManager.addTempPassword(mainObject.instanceID, cryptoKey);
                    else
                        PasswordManager.addPassword(mainObject.objectName, mainObject.id, cryptoKey);
                }

                if (mainObject !== actualObject)
                    actualObject.decrypt();

                return decrypt_result;
            };
        } else {
            action = async (password: string): Promise<boolean> => { // enter password action
                if (!password) // pokud je heslo prázdné
                    return false;
                const cryptoKey = await SecurityUtils.passwordToDeriveKey(password, (await actualObject.getValue("ObjectID")).valueOf());//await SecurityUtils.passwordToCryptoKey(password);
                if (mainObject.isNew)
                    PasswordManager.addTempPassword(mainObject.instanceID, cryptoKey);
                else
                    PasswordManager.addPassword(mainObject.objectName, mainObject.id, cryptoKey);
                return true;
            };
        }

        if (encrypt) {
            closeAction = async () => {
                const result = await MessageWindow.showYesNoDialog(
                    new LocalizedStringBuilder(
                        "GUI-633053"), // Při zavření dialogu pro zadání hesla přijdete o zadanou hodnotu. Chcete znovu zadat heslo?
                    "GUI-633051" /*Zadat heslo*/, "GUI-633052" /*Zahodit změny*/);
                return result.result !== MessageButtonType.ok; /*ync*/
            };
        }

        const msgLocKey = encrypt
            ? "GUI-632958" //Zadejte heslo pro zašifrování záznamu '@0'.
            : "GUI-631104"; //Záznam je zabezpečen šifrováním. Zadejte heslo pro dešifrování záznamu '@0'.

        const builder = new PasswordDialogBuilder(msg, msgLocKey, mainObject, PasswordDialogBuilderType.verify, action, closeAction,
            DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.encryption));

        msg.header = "GUI-631101"; //Zadejte heslo
        msg.contentBuilder = builder;
        msg.detailButton = false;
        msg.type = MessageType.message;
        msg.okButtonCaption = encrypt ? "GUI-1" /*OK*/ : "GUI-631103";
        msg.otherButtonCaption = "GUI-33412"; //Zrušit

        return (await msg.show()).result === MessageButtonType.ok;
    }

    public static async findMainObject(actualObject: OWObject): Promise<OWObject> {
        if (!actualObject.classInfo.passwordMasterLink)
            return actualObject;
        const mainObject = (await actualObject.getValue(actualObject.classInfo.passwordMasterLink) as OWObject);
        if (mainObject == null || mainObject instanceof NullOWObject) {
            return actualObject;
        }
        return await FrameSharedMethods.findMainObject(mainObject);
    }

    /**
     * Odstraní aktuální položky WF
     */
    public static clearWorkflowItems(actions: Set<IWFAction>, menu: IMenu): void {
        for (const oldWf of actions) {
            menu.removeAction(oldWf);
        }
        actions.clear();
    }

    /**
     * Vytvareni a pridavani WF akci do menu záznamu
     */
    public static async createWorkflowItems(workflowItems: IWorkflowMenuItem[], owner: IMasterableFrame, ownerForm: IForm): Promise<Set<IWFAction>> {
        const actions = new Set<IWFAction>();

        if (workflowItems != null) {
            const wfEnableVisualStatus = this._getWFActionEnableVisual(owner, ownerForm);
            for (const item of workflowItems) {
                const wfAction = new WFAction(item, owner);
                // porovnání owner.ownerForm !== ownerForm řeší případ, kdy je otvírán modální detailový formulář a owner je framem, ze kterého je otvíraný a který je v tu chvílí v readOnly režimu
                wfAction.enabledVisual = wfAction.enabled = wfEnableVisualStatus;
                actions.add(wfAction);
            }
        }
        return actions;
    }

    /**
     * Vytváření a přidávání MultiWF akcí do toolbaru
     */
    public static async createMultiWorkflowItems(workflowItems: IWorkflowMenuItem[], owner: IMasterableFrame, ownerForm: IForm, objectList: OWObjectList, currentActions: WFMultiAction[]): Promise<Set<WFMultiAction>> {
        const actions = new Set<WFMultiAction>();

        if (workflowItems != null) {
            const wfEnableVisualStatus = this._getWFActionEnableVisual(owner, ownerForm);
            for (const item of workflowItems) {
                const wfAction = new WFMultiAction(item, owner, objectList);
                wfAction.enabledVisual = wfAction.enabled = wfEnableVisualStatus;

                // ošetřuje situaci, ke které dochází kvůli souběhu při označování záznamů,
                // kdy při rychlooznačení více záznamů najednou je jedna WFMultiAction přidávána do toolbaru několikrát
                let multiWfAlreadyInMenu = false;
                const menuActions = owner.menu.getActions();
                for (const menuAction of menuActions) {
                    if (menuAction instanceof WFMultiAction && menuAction.wfMenuItem.ID === wfAction.wfMenuItem.ID) {
                        menuAction.toolbarButton.action = wfAction;
                        multiWfAlreadyInMenu = true;
                        break;
                    }
                }

                if (!multiWfAlreadyInMenu) { // pokud už je WFMultiAction v menu, nebude se přidávat znova
                    actions.add(wfAction);
                    owner.menu.addAction(wfAction);
                } else {
                    const action = currentActions.find(item => item.wfMenuItem.ID === wfAction.wfMenuItem.ID);
                    if (action)
                        action.objectList = wfAction.objectList; // aktualizujeme vybrané záznamy
                }
            }
        }
        return actions;
    }

    private static _getWFActionEnableVisual = (owner: IMasterableFrame, ownerForm: IForm) => (ownerForm && owner.ownerForm !== ownerForm) ? !ownerForm.readOnly : owner.interactive;

    /**
     * Určí, zda se jedná o název formuláře pro použití implicitního formuláře.
     * Viz https://teaf.tescosw.cz/wiki/index.php?title=Implicitn%C3%AD_formul%C3%A1%C5%99
     * @param formName Jméno formuláře k otestování. Protože tato část kódu ještě není striktní, zvládá metoda zpracovat i undefined/null i když není součástí signatury. Pro tyto případy vrací false.
     */
    public static isFormNameImplicit(formName: string): boolean {
        return !!formName?.toUpperCase().startsWith("IMPLICIT");
    }

    /**
     * Zjistí, jaký formulář se ma použít pro zobrazeni detailu aktualního záznamu.
     */
    public static async getDefaultFormName(formName: string, ownerForm: IForm, actObj: OWObject | IActionContext, frameContext: FrameContext, masterAttr?: string): Promise<IImplicitFormInfo> {
        if (FrameSharedMethods.isFormNameImplicit(formName)) {
            const ownerFormName = ownerForm && ownerForm?.formName;
            if (!ownerFormName)
                throw new InvalidOperationException("Property ownerForm is required in order to get IMPLICIT form.");
            //const formInfo = await actObj.getImplicitForm("", ownerForm.formName + formName.substring(8));
            const requestContext = convertContextFromFrameToRequest(frameContext);
            const formInfo = actObj instanceof IActionContext
                ? await OWManager.getRecordImplicitForm(actObj.ids[0] ?? "", actObj.className, masterAttr ?? "", `${ownerFormName}${formName.substring(8)}`, requestContext)
                : await actObj.getImplicitForm(masterAttr ?? "", `${ownerFormName}${formName.substring(8)}`); // `
            if (!formInfo || !formInfo.FormName)
                throw new EOWNoImplicitFormForClassException("");
            return formInfo;
        } else {
            return { FormName: formName };
        }
    }

    /**
     * Vyextrahuje vhodné id instance master objektu. Pokud je specifikovaný i masterAttr, tak se bere id instance v atributu uvedeném v masterAttr.
     * @param baseObj Základní aplikační objekt, na němž se hledá vazba
     * @param masterAttr Název vazby, ve které se má hledat hlavní záznam
     * @param requestContext Viz {@link RequestContext}
     */
    public static async getMasterAttrInstanceId(baseObj: IOWObject | null, masterAttr?: string, requestContext: RequestContext = EMPTY_REQUEST_CONTEXT): Promise<{ instanceId: string | null; upgradedCt?: CancellationToken }> {
        if (baseObj == null)
            return { instanceId: null };

        let ct = requestContext.cancellationToken;

        const cts = new CancellationTokenSource(ct ? [ct] : undefined);
        ct = cts.token;
        baseObj.disposed.add(() => cts.cancel());

        if (!masterAttr)
            return { instanceId: baseObj.instanceID, upgradedCt: ct };

        const masterObj = await baseObj.getValue(masterAttr);

        if (masterObj != null && masterObj instanceof IOWObject) {
            const cts2 = new CancellationTokenSource(ct ? [ct] : undefined);
            ct = cts2.token;
            masterObj.disposed.add(() => cts.cancel());

            await OWManager.publishInstance(baseObj.instanceID, masterAttr, requestContext);
            return { instanceId: masterObj.instanceID, upgradedCt: ct };
        }

        return { instanceId: null };
    }

    private static _createAction(actionType: ActionType, action: () => Promise<void>, isRecordAction: boolean, owner: IFrame): StandardAction;
    private static _createAction(actionType: ActionType, action: (e: Event, dataKey: string) => Promise<void>, isRecordAction: boolean, owner: IFrame): StandardAction;
    private static _createAction(actionType: ActionType, action: any, isRecordAction: boolean, owner: IFrame): StandardAction {
        const stdAction = this._getStandardActionDefinition(actionType);
        if (isRecordAction) {
            const context = stdAction.context ? stdAction.context : ActionContext.objectAction;
            return new StandardRecordAction(actionType, action, undefined, context, undefined, owner);
        }
        return new StandardAction(actionType, action, undefined, stdAction.context, undefined, owner);
    }

    private static _getStandardActionDefinition(actionType: ActionType): StandardActionDefinitionBase {
        const stdAction = FrameSharedMethods._actionDefinitions.get(actionType);
        if (!stdAction)
            throw new InvalidOperationException("Unable to find definition for ActionType " + ActionType[actionType]);
        return stdAction;
    }

    /**
     * Vytvoří standardní akci určenou pro spouštění nad záznamem, s popiskem nastaveným pro daný typ akce
     * @param actionType Účel akce
     * @param action Funkce vyjadřující akci
     */
    public static createStandardRecordAction(actionType: ActionType, action: (e: Event, dataKey: string) => Promise<void>, owner: IFrame): StandardRecordAction {
        return FrameSharedMethods._createAction(actionType, action, true, owner) as StandardRecordAction;
    }

    /**
     * Vytvoří standardní akci s popiskem nastaveným pro daný typ akce
     * @param actionType Účel akce
     * @param action Funkce vyjadřující akci
     * @param owner Frame vlastnící akci
     */
    public static createStandardAction(actionType: ActionType, action: (params?: any) => Promise<void>, owner: IFrame): StandardAction {
        return FrameSharedMethods._createAction(actionType, action, false, owner);
    }

    /**
     * Vytvoří standartní akci pro dešifrování objektu
     * @param action
     */
    public static createDecryptToolbarAction(action: () => Promise<void>, owner: IFrame): StandardAction {
        const decryptAction = FrameSharedMethods.createStandardAction(ActionType.standardDecrypt, action, owner);
        decryptAction.toMenu = MenuType.mtNone;
        return decryptAction;
    }

    public static createFilterAction(actionType: ActionType, action: () => Promise<void>, owner: IFrame): StandardAction {
        const filterAction = FrameSharedMethods._createAction(actionType, action, false, owner);
        filterAction.toMenu = MenuType.mtFilter;
        return filterAction;
    }

    /**
     * Vytvoří akci spouštěnou nad všemi aktuálně vybranými záznamy (minimálně však dva)
     * @param actionType Účel akce
     * @param action Funkce vyjadřující akci
     */
    public static createMultiSelectAction(actionType: ActionType, action: () => Promise<void>, owner: IFrame): StandardAction {
        const multiAction = FrameSharedMethods._createAction(actionType, action, false, owner);
        multiAction.supportedSelectionModes = SelectionType.multi;

        return multiAction;
    }

    /**
     * Vytvoří standardní dvoustavovou akci
     * @param actionType Účel akce
     * @param action Funkce vyjadřující akci
     * @param owner Frame vlastnící akci
     * @param defaultState Defaultní stav akce
     */
    public static createStandardTwoStateAction(actionType: ActionType, action: any, owner: IFrame, defaultState?: boolean): StandardTwoStateAction {
        const stdAction = this._getStandardActionDefinition(actionType);
        return new StandardTwoStateAction(actionType, action, undefined, stdAction.context, undefined, owner, defaultState);
    }

    public static async addFulltextSearch(frame: IFulltextSearchableFrame & (IMasterableFrameWithDataGrid | IReactFrameConnector)): Promise<void> {
        if (!frame.hideToolBar && frame.menu && frame.fulltextAttributes.length > 0) {
            const attributesToCheck = Array.from(frame.fulltextAttributes);
            const knownAttributes: Map<string, OWDataType | string> = new Map<string, OWDataType | string>();
            const displaySubattributes: Map<string, string> = new Map<string, string>();

            const loadSubattribute = async (attrName: string): Promise<string> => {
                const classDescription = await ModelDescriptionsManager.instance.getClassInfo(frame.objectName);
                if (classDescription) {
                    const attributeInfo = await classDescription.getAttributeInfo(attrName);
                    if (attributeInfo) {
                        displaySubattributes.set(attrName, attributeInfo.displayAttribute);
                        return attributeInfo.displayAttribute;
                    }
                }

                return "";
            };

            // Escapuje apostrof a wildcards kvůli SQL na DB
            const escapeSearchString = (searchString: string): string => {
                let resultString: string = searchString.replace(/(')/g, "''"); // Zdvojí apostrofy
                resultString = resultString.replace(/(%|_)/g, "\\$&"); // Přidá před znaky % a _ zpětné lomítko
                return resultString;
            };

            const initFulltext = async () => {
                // Získání typů atributů, které jsou zobrazeny na DataGridu
                if (frame instanceof IMasterableFrameWithDataGrid) {
                    for (const column of frame.currentDataGrid.columns) {
                        const columnAttrNameUpper = column.attrName.toUpperCase();
                        let attributeIndex = -1;
                        for (let i: number = 0; i < attributesToCheck.length; ++i) {
                            if (attributesToCheck[i].toUpperCase() === columnAttrNameUpper) {
                                attributeIndex = i;
                                break;
                            }
                        }
                        if (attributeIndex > -1) {
                            knownAttributes.set(column.attrName, column.fullDataType);
                            if (EnumUtils.getEnumValueFromName(OWDataType, column.fullDataType) === OWDataType.localizedText) {
                                await loadSubattribute(column.attrName);
                            }
                            attributesToCheck.splice(attributeIndex, 1);
                            if (attributesToCheck.length <= 0)
                                break;
                        }
                    }
                }
                // Dočtení informací o atributech, které nejsou zobrazeny na DataGridu
                if (attributesToCheck.length > 0) {
                    const remainingAttributes = await ModelDescriptionsManager.instance.getAttributesInfo(frame.objectName, attributesToCheck);

                    for (const attributeInfo of remainingAttributes) {
                        knownAttributes.set(attributeInfo.attributeName, attributeInfo.type);
                        if (attributeInfo.type === OWDataType.localizedText) {
                            await loadSubattribute(attributeInfo.attributeName);
                        }
                    }
                }

                const fullsearchAction = new Action(async (searchString: string): Promise<void> => {
                    //logger.log(`Attempt for fulltext search of "${searchString}" on frame "${frame.frameName}"`); // `
                    let fulltextCondition = "";
                    if (searchString) {
                        for (const [attributeName, dataType] of knownAttributes) {
                            let subCondition = "";
                            const owAttrDataType = EnumUtils.getEnumValueFromName(OWDataType, dataType);
                            if (
                                owAttrDataType === OWDataType.string
                                || owAttrDataType === OWDataType.text
                                || owAttrDataType === OWDataType.htmlText
                            ) {
                                subCondition = `:Linguistic.LIKE(${attributeName}, '%${escapeSearchString(searchString)}%')`; // `
                            } else if (owAttrDataType === OWDataType.localizedText) {
                                const displayAttribute = displaySubattributes.get(attributeName);
                                if (displaySubattributes)
                                    subCondition = `:Linguistic.LIKE(${attributeName}.${displayAttribute}, '%${escapeSearchString(searchString)}%')`; // `
                                else
                                    logger.error(`Missing subattribute name for attribute "${attributeName}" of type LocalizedText!`); // `
                            } else if (owAttrDataType === OWDataType.enum) {
                                const attrInfo = await ModelDescriptionsManager.instance.getAttributesInfo(frame.objectName, [attributeName]);
                                const enumName = attrInfo[0].additionalTypeName;
                                const enumType = await OWManager.getEnum(enumName);

                                const compareFnc = (value: string, enumValue: string): boolean => {
                                    enumValue = StringUtils.removeAccents(enumValue.toUpperCase());
                                    value = StringUtils.removeAccents(value.toUpperCase());
                                    return enumValue.indexOf(value) !== -1;
                                };

                                const vals = [-1];
                                for (const item of enumType.descriptions) {
                                    if (compareFnc(searchString, item.Description))
                                        vals.push(item.IntValue);
                                }

                                const combinedEnumSearchString = vals.join(", ");

                                subCondition = `${attributeName} ∈ { ${combinedEnumSearchString} }`; // `
                            } else {
                                logger.error(`Type "${dataType}" of attribute "${attributeName}" is not supported for fulltext search!`); // `
                            }
                            fulltextCondition = StringUtils.combineConditions(ConditionOperator.or, fulltextCondition, subCondition); // `
                        }
                    }

                    frame.fulltextCondition = {
                        searchString,
                        condition: fulltextCondition,
                    };

                    return await frame.updateConditionAndRead();
                });

                fullsearchAction.actionType = ActionType.standardFulltextSearch;

                frame.menu.addAction(fullsearchAction);
            };

            if (frame instanceof IMasterableFrameWithDataGrid) {
                frame.dataGridInitialized.add(async () => {
                    await initFulltext();
                });
            } else {
                await initFulltext();
            }
        }
    }

    /**
     * Vytvoří kolekci s inicializačními parametry pro datasource seznamu
     * Počítá i s formConditionParams
     * @returns seznam hodnot parametrů pro podmínky nebo null, pokud nebylo možné hodnoty načíst
     * @internal
     */
    public static async createReaderConditionParams(frame: IBaseFrame | HasMasterFrame): Promise<ConditionParams> {
        if (frame.isMainFrame) {
            if (frame.ownerForm.formConditionParamAttrs && frame.conditionParamsAttrs)
                logger.warn(`[Frame] Main frame '${frame.frameName}' has both ConditionParams from parent (action) '${frame.ownerForm.formConditionParamAttrs}' and its own '${frame.conditionParamsAttrs}' - using params only from parent`);

            // pokud existuji formConditionParamAttrs, tak se uplatni pouze ty a uz se neaplikuje condition a conditionParams (neni je jak mergnout)
            if (frame.ownerForm.formConditionParamAttrs && frame.ownerForm.formConditionParamsObject)
                return await frame.ownerForm.formConditionParamsObject.getParameters(frame.ownerForm.formConditionParamAttrs);

            // aplikuje ConditionParams na hlavni frame formulare (používá to minimálně levý seznam TLF)
            if (!frame.ownerForm.formConditionParamAttrs && frame.conditionParamsAttrs && frame.ownerForm.masterObject) {
                return await frame.ownerForm.masterObject.getParameters(frame.conditionParamsAttrs);
            }
        } else {
            // podrizeny seznam
            if (frame.conditionParamsAttrs) {
                let cObj: OWObject;
                if (frame.masterFrame) { // podrizeny seznam
                    cObj = frame.masterFrame.actualObject;
                } else if (frame.ownerForm.detailFrame instanceof ISeparateDetailFrame) {
                    //await (this.ownerForm.detailFrame as SeparateDetailFrame).formUiRegistered;
                    cObj = frame.ownerForm.detailFrame.actualObject;
                } else {
                    if (frame.ownerForm.formConditionParamsObject)
                        cObj = frame.ownerForm.formConditionParamsObject;
                    else
                        cObj = frame.ownerForm.masterObject;
                }
                if (cObj == null || cObj instanceof NullOWObject) {
                    return null;
                } else {
                    let masterObject = cObj;
                    if (frame.masterAttr != null && frame.masterAttr !== "")
                        masterObject = await cObj.getValue(frame.masterAttr) as OWObject;

                    if (!(masterObject instanceof IOWObject))
                        return null;

                    return await masterObject.getParameters(frame.conditionParamsAttrs);
                }
            }
        }
        return [];
    }

    /**
     * Doplní vygenerovanou podmínku částí pro práva na čtení
     * @param condition k doplnění
     * @internal
     */
    public static appendReadRightCondition(condition: string, frame: IBaseFrame | HasMasterFrame): string {
        // je potřeba přidat podmínky z práv na čtení
        // nejdříve pro defaultní roli
        const formRightWithoutRole = frame.ownerForm.formRights.find(r => r.roleID === ROLELESS_ROLE_ID);

        if (formRightWithoutRole && formRightWithoutRole.readConditionID) {
            condition = FrameSharedMethods.combineRightCondition(condition, formRightWithoutRole.getReadRight());
        } else {
            // a jinak z ostatních aktivních rolí
            for (const r of frame.ownerForm.formRights) {
                if (r.roleID !== ROLELESS_ROLE_ID && r.readConditionID) {
                    condition = FrameSharedMethods.combineRightCondition(condition, r.getReadRight());
                }
            }
        }

        return condition;
    }

    /**
     * Vytvoří akci umožňující hromadné stahování souborů z gridu
     * @param frame Frame akce
     */
    public static createDownloadAction(frame: IMasterableFrameWithDataGrid & IFrameWithProfile): IAction | null {
        if (frame.hideToolBar || !frame.menu)
            return null;

        const action = new BatchDownloadAction(frame);
        action.visible = false;

        return action;
    }

    /**
     * Vyhodnotí podmínky a vrátí kolekci jejich výsledků
     * @param conditionItems Seznam použitých podmínek na framu
     * @param actualObject Actual object framu
     * @param requestContext Viz {@link RequestContext}
     */
    public static async evaluateConditions(conditionItems: Array<IRightConditionItem>, actualObject: OWObject, requestContext?: RequestContext): Promise<IEvaluatedConditionResult[] | null> {
        if (conditionItems && conditionItems.length > 0) {
            const promises: Promise<IEvaluatedConditionResult>[] = [];
            for (const condItem of conditionItems) {
                let evaluateConditionFunc: (condId: string, requestContext?: RequestContext | CancellationToken) => Promise<boolean>;

                if (condItem.isStatic) {
                    evaluateConditionFunc = OWManager.evaluateConditionWithoutObject.bind(OWManager); // Pokud se jedná o statickou podmínku (podmínka na statickém prvku), vyhodnocuje se na serveru nezávisle na objektu
                } else {
                    if (actualObject)
                        evaluateConditionFunc = actualObject.evaluateCondition.bind(actualObject); // Pokud je k dispozici objekt, vyhodnocuje se na serveru v závislosti na něm
                    else
                        evaluateConditionFunc = () => {
                            return Promise.resolve(null);
                        }; // Výsledek vyhodnocení podmínky nestatické metody bez aktuálního objektu bude null / neurčitý / nevyužitý
                }

                promises.push(evaluateConditionFunc(condItem.id, requestContext).then((result: boolean) => ({ Result: result, ID: condItem.id })));
            }
            return Promise.all(promises);
        }

        return null;
    }

    /**
     * Provede rozparsování orderBy
     * @param dataSource
     * @param ordering
     */
    public static parseOrderBy(ordering: string, defaultOrderBy: IOrderBy[] = []): IOrderBy[] {
        if (ordering != null) {
            const parts = ordering.split(";").filter(v => v); //vrátí neprazdne dílčí order by.
            if (!parts.length) {
                logger.warn(`Unable to parse "${ordering}", using default ordering`);
                return defaultOrderBy;
            }
            try {
                return parts.map(v => {
                    const groups = /^desc ([\w-|]+(?:\.[\w-|]+)*)|^(?:asc |)([\w-|]+(?:\.[\w-|]+)*)$/i.exec(v.trim());
                    if (groups?.[1]) { // "DESC xxx"
                        return {
                            attributeName: groups[1],
                            order: OrderType.descending,
                        };
                    }
                    return { // group[2] - "ASC xxx" || "xxx"; v - jinak
                        attributeName: groups[2] || v,
                        order: OrderType.ascending,
                    };
                });
            } catch {
                logger.warn(`Unable to parse "${ordering}", using default ordering`);
                return defaultOrderBy;
            }
        } else {
            return defaultOrderBy;
        }
    }

    public static parseEmptyMessageCustomization(element: HTMLElement): EmptyMessageCustomization {
        if (!element) {
            return {};
        }
        return {
            noItemsIcon: EnumUtils.getEnumValueFromValue(IconCode, element.getAttribute("data-emptymessageicon")),
            noItemsLocKey: element.getAttribute("data-emptymessagelc"),
            noItemsFilterIcon: EnumUtils.getEnumValueFromValue(IconCode, element.getAttribute("data-emptyfiltermessageicon")),
            noItemsFilterLocKey: element.getAttribute("data-emptyfiltermessagelc"),
            noItemsFulltextSearchIcon: EnumUtils.getEnumValueFromValue(IconCode, element.getAttribute("data-emptyfulltextmessageicon")),
            noItemsFulltextSearchLocKey: element.getAttribute("data-emptyfulltextmessagelc"),
        };
    }

    public static registerStandardListColumnTypes(): void {
        if (FrameSharedMethods._columnTypesRegistered)
            return;

        FrameSharedMethods._columnTypesRegistered = true;

        // Podporované datové typy https://wiki-dokumentace/index.php?title=Podporovan%C3%A9_datov%C3%A9_typy

        //const ColumnFactory = ColumnFactory;

        ColumnFactory.add("LOVAttribute", LovColumn);
        ColumnFactory.add("LinkAttribute", LinkColumn);
        ColumnFactory.add("TextLOVAttribute", TextLovColumn);

        ColumnFactory.add("Enum", EnumColumn);
        ColumnFactory.add("Boolean", BooleanColumn);
        ColumnFactory.add("Flags", FlagColumn);

        ColumnFactory.add("Date", DateColumn);
        ColumnFactory.add("DateTime", DateTimeColumn);
        ColumnFactory.add("Time", TimeColumn);

        ColumnFactory.add("Decimal", NumberColumn);
        ColumnFactory.add("Money", NumberColumn);

        ColumnFactory.add("BCD", NumberColumn);
        ColumnFactory.add("Double", NumberColumn);
        ColumnFactory.add("Single", NumberColumn);

        ColumnFactory.add("Integer", NumberColumn);
        ColumnFactory.add("Int64", NumberColumn);
        ColumnFactory.add("Short", NumberColumn);
        ColumnFactory.add("Byte", NumberColumn);

        ColumnFactory.add("Document", FileColumn);
        ColumnFactory.add("FileEditAttribute", FileColumn);
    }

    /**
     * Rozparsuje hideButtons atribut a vrátí nově vytvořený objekt HideButtons
     * @param ownerForm - form na kterém leží MasterableFrame
     * @param hideButtons - atribut k rozparsování
     * @param hideButtonsOverride - zda použít hodnotu z formu
     */
    public static parseHideButtons(ownerForm: IForm, hideButtons: string, hideButtonsOverride: boolean): HideButtons {
        return new HideButtons(
            hideButtons ? hideButtons.toLocaleLowerCase().replace(/ /gi, "").split(",") : [],
            ownerForm && hideButtonsOverride ? ownerForm.hideButtons : undefined);
    }

    /**
    * Vytvori kolekci ExtendedOperation, ktere se maji pouzit pri getovani existujiciho nebo vytvareni noveho zaznamu.
    * Bere v potaz i vazbu na nadrizeny frame a pripad otevriani formulare z akce
    */
    public static async createAfterCreateExtendedOperations(parms: IExtendedOperationsParams, isNew: boolean): Promise<IExtendedObjectOperation[] | null> {
        //after GUI metody uplatnit pokud vytvarim objekt stejne tridy, jake jsou zobrazeny v ListFrame
        const extendedOperations: IExtendedObjectOperation[] = [];
        if (isNew && parms.afterGuiCreateMethod)
            extendedOperations.push({ Operation: OWExtendedObjectOperationsType.AfterCreateMethods, Value: parms.afterGuiCreateMethod });

        const setAttributesAfterCreate: string[] = [];

        if (isNew && parms.afterGuiCreateFill)
            setAttributesAfterCreate.push(parms.afterGuiCreateFill);

        if (isNew && parms.isMainFrame && parms.ownerForm.setValues)
            setAttributesAfterCreate.push(parms.ownerForm.setValues);

        /** !!! Nastavení master vazby musí být vždy jako první akce. Bohužel na to spoléhá nemalá část aplikační logiky. */
        const setMasterAttributesAfterCreate = [...(await FrameSharedMethods._createGetObjectSetAttributes(parms))]
            .map(([attrName, attrValue]) => `${attrName}=${attrValue}`);

        const setAttributes = [...setMasterAttributesAfterCreate, ...setAttributesAfterCreate];

        // každý typ extended operace může být předán pouze jednou
        if (setAttributes.length > 0)
            extendedOperations.push({ Operation: OWExtendedObjectOperationsType.SetAttributesAfterCreate, Value: setAttributes.join(";") });

        return extendedOperations.length > 0 ? extendedOperations : null;
    }

    /**
     * Vytvoří hodnoty pro ExtendedOperation pro nastavení vazby na instanci master záznamu
     */
    private static async _createGetObjectSetAttributes(parms: IExtendedOperationsParams): Promise<Map<string, string>> {
        const operations: Map<string, string> = new Map();

        // pro hlavni frame se musi uplatnit vazba predana pri otevreni formulare (treba z DFA nebo PFA)
        if (parms.isMainFrame) {
            if (parms.ownerForm.masterLinkToAttr != null && parms.ownerForm.masterLinkToAttr != "" && parms.ownerForm.masterObject != null) {
                if (!parms.isMasterObjectList && parms.ownerForm.masterLinkToAttr != "ID")
                    operations.set(parms.ownerForm.masterLinkToAttr, parms.ownerForm.masterObject.instanceID);
            }
        } else { // pro podrizene framy se uplatni vazba na nadrizeny zaznam
            // pokud je v masterLinkToAttr hodnota "ID", tak se nebude přidávat do operations. Jedná se totiž o "ohnuté" použití dodávkovými úseky a způsobovalo by to chybu na serveru. Správně by měla být celá vazba.
            // pokud je zapnuto zobrazení včetně podřízených, tak nadřízený záznam přidat nemůžeme - ve vazbě nemusí být přímo on, ale také jeden z jeho podřízených
            if (parms.masterLinkToAttr == "ID" || parms.showSubrecordsActive)
                return operations;

            if (parms.masterLinkToAttr != null && parms.masterFrame != null && parms.masterFrame.actualObject != null) {
                let masterObject = parms.masterFrame.actualObject;

                if (parms.masterAttr) {
                    // může se stát, že v masterAttr je instance, která není vypublikovaná na serveru, pak to při nastavení do vazby spadne
                    if (masterObject?.instanceID)
                        await OWManager.publishInstance(masterObject.instanceID, parms.masterAttr);

                    masterObject = await masterObject.getValue(parms.masterAttr) as OWObject;
                }
                if (masterObject && masterObject.instanceID && !parms.isMasterObjectList)
                    operations.set(parms.masterLinkToAttr, masterObject.instanceID);
            } else {
                if (parms.masterLinkToAttr != null && parms.masterObjectInstanceIDForLinkToAttr != null) {
                    operations.set(parms.masterLinkToAttr, parms.masterObjectInstanceIDForLinkToAttr);
                }
            }
        }

        return operations;
    }

    /**
     * Zobrazí potvrzovací dialog pro uložení změn
     */
    public static showSaveChangesDialog(): Promise<MessageButtonResult> {
        /**
         * GUI-319216 - Přejete si uložit změny?
         * GUI-6 - Uložit
         * GUI-605905 - Neukládat
         * GUI-33412 - Zrušit
         */
        return MessageWindow.showYesNoCancelDialog(new LocalizedStringBuilder("GUI-319216"), "GUI-6", "GUI-605905", "GUI-33412");
    }

    /**
     * Provede rozparsování vlastností refreshActual a refreshList do enumu
     * @param refreshActual
     * @param refreshList
     * @param defaultType - defaultní hodnota, která má být vrácena, pokud ani jedna z vlastností není nastavená
     */
    public static parseFrameRefreshType(refreshActual: string, refreshList: boolean, defaultType: FrameRefreshType = FrameRefreshType.none): FrameRefreshType {
        if (refreshList) {
            return FrameRefreshType.listOnChange;
        } else {
            if (refreshActual) {
                refreshActual = refreshActual.toLocaleLowerCase();
            }

            if (refreshActual === "onformclose" || refreshActual === "true") {
                return FrameRefreshType.recordOnChange;
            } else if (refreshActual === "false") {
                return FrameRefreshType.none;
            } else {
                return defaultType;
            }
        }
    }

    /**
    * Provede rozparsování parseFormLayout do enumu
    * @param formLayout
    * @param defaultType - defaultní hodnota, která má být vrácena, pokud ani jedna z vlastností není nastavená
    */
    public static parseFormLayout(formLayout: string, defaultType: FormLayout = FormLayout.none): FormLayout {
        if (formLayout) {
            formLayout = formLayout.toLocaleLowerCase();
        }

        if (formLayout === "horizontal") {
            return FormLayout.horizontal;
        } else if (formLayout === "vertical") {
            return FormLayout.vertical;
        } else {
            return defaultType;
        }
    }

    /**
     * Nastavuje na rodičovský frame příznak, zda má být při změně proveden jeho refresh
     */
    public static refreshFrameAfterSetActive(ownerForm: IForm): void {
        if (ownerForm.frameToRefreshAfterSetFormActive) {
            if (ownerForm.frameRefreshType === FrameRefreshType.listOnChange) {
                ownerForm.frameToRefreshAfterSetFormActive.refreshListAfterSetActive = true;
            } else if (ownerForm.frameRefreshType === FrameRefreshType.recordOnChange) {
                ownerForm.frameToRefreshAfterSetFormActive.refreshObjectAfterSetActive = true; // nastaví se příznak, že má dojít k refreshi (po zavření Formuláře nebo po přepnutí breadcrumbs)
            }
        }
    }

    /**
     * Naplní DropDownMenu sloupce položkami pro filtrování
     * @param dropDown Menu, do kterého se mají položky plnit
     */
    public static createColumnDropDownMenuFiltering(dropDown: DropDownMenu, frameContext: FrameContext): DropDownMenuFilterItem {
        dropDown.addItem(SeparatorMenuItemWithText.createSeparatorWithText("GUI-280088")); // Filtrovat
        const filterItem = new DropDownMenuFilterItem();
        filterItem.frameContext = frameContext;
        filterItem.attrName = "";
        filterItem.filterBlock = null;
        dropDown.addItem(filterItem); // filtrování
        return filterItem;
    }

    /**
     * Naplní DropDownMenu sloupce položkami pro řazení
     * @param dropDown Menu, do kterého se mají položky plnit
     * @param dataGrid DataGrid, kterému se nastavuje řazení
     * @param currentColumnAccessor Funkce pro přístup ke sloupci, na který bylo právě kliknuto
     * @param orderingChangedCallback Funkce volaná po změně řazení
     */
    public static createColumnDropDownMenuOrdering(dropDown: DropDownMenu, dataGrid: IListDataGrid<IListDataRowElement>, className: string, currentColumnAccessor: () => Column, orderingChangedCallback: (ordering: IOrderBy[]) => void): void {
        const ordDesc = DropDownMenuItem.createDropMenuItem(Icon[Icon.arrowUp], async () => {
            const ord: IOrderBy[] = [];
            const objectClass = await ModelDescriptionsManager.instance.getClassInfo(className);

            const attrType = (await objectClass.getAttributeInfo(currentColumnAccessor().attrName)).basicType;

            ord.push({
                attributeName: currentColumnAccessor().orderAttrName,
                order: OrderType.ascending,
                additionalOrdering: attrType == BasicDataType.String || attrType == BasicDataType.StrNum ? AdditionalOrdering.linguistic : null,
            });

            orderingChangedCallback(ord);
        }, "GUI-584564", true);
        const ordAsc = DropDownMenuItem.createDropMenuItem(Icon[Icon.arrowDown], async () => {
            const ord: IOrderBy[] = [];
            const objectClass = await ModelDescriptionsManager.instance.getClassInfo(className);

            const attrType = (await objectClass.getAttributeInfo(currentColumnAccessor().attrName)).basicType;

            ord.push({
                attributeName: currentColumnAccessor().orderAttrName,
                order: OrderType.descending,
                additionalOrdering: attrType == BasicDataType.String || attrType == BasicDataType.StrNum ? AdditionalOrdering.linguistic : null,
            });

            orderingChangedCallback(ord);
        }, "GUI-584565", true);

        const noOrd = DropDownMenuItem.createDropMenuItem(Icon[Icon.none], async () => {
            const ord: IOrderBy[] = [];
            for (const o of dataGrid.orderBy) {
                if (o.attributeName == currentColumnAccessor().orderAttrName)
                    continue;
                else
                    ord.push(o);
            }

            orderingChangedCallback(ord);
        }, "GUI-602240", true);

        const cannotSort = DropDownMenuItem.createDropMenuItem(Icon[Icon.none], undefined,
            "GUI-740862", // Podle tohoto sloupce nelze řadit
            true);

        // reakce menu na readonly
        dataGrid.registerEventListener("interactiveChanged", (e: IBooleanDetailValueEvent) => {
            ordDesc.enabled = !e.detail.value;
            ordAsc.enabled = !e.detail.value;
        });

        ordDesc.classList.add("SortMenuItem");
        ordAsc.classList.add("SortMenuItem");
        noOrd.classList.add("SortMenuItem");
        cannotSort.classList.add("NoSortMenuItem");

        dropDown.addItem(SeparatorMenuItemWithText.createSeparatorWithText("GUI-602242")); // razeni
        dropDown.addItem(ordDesc); // vzestupne
        dropDown.addItem(ordAsc); // sestupne
        dropDown.addItem(noOrd); // neradit
        dropDown.addItem(cannotSort); // Podle tohoto sloupce nelze řadit
    }

    /**
     * Vytvoří a naplní strukturu s daty pro tisk předávanou aplikačnímu serveru
     * @param data Data, potřebná k vygenerování struktury pro tisk
     * @param type Typ tisku. Pokud není nastaven, použije se hodnota uložená v AppStorageItemType.reportType
     */
    private static _getReportParams(data: IExportData, type?: SupportedReportFormats): ITableExportParams {
        return FrameSharedMethods._getReportExportParams(data, type ? SupportedReportFormats[type] : ApplicationStorage.get(AppStorageItemType.reportType));
    }

    /**
     * Vytvoří a naplní strukturu s daty pro export předávanou aplikačnímu serveru
     * @param data Data, potřebná k vygenerování struktury pro export
     * @param type Typ exportu
     */
    private static _getExportParams(data: IExportData, type: SupportedExportFormats): ITableExportParams {
        return FrameSharedMethods._getReportExportParams(data, SupportedExportFormats[type]);
    }

    /**
     * Vytvoří a naplní strukturu s daty pro tisk/export předávanou aplikačnímu serveru
     * Pokud je na framu viditelný agregační řádek, zahrne agregační funkce do tisku seznamu.
     * @param data Data, potřebná k vygenerování struktury pro tisk/export
     * @param type Typ tisku/exportu
     */
    private static _getReportExportParams(data: IExportData, type: string): ITableExportParams {
        const columns: ITableExportAttrDesc[] = [];
        let printAggregationRow: boolean = false;
        let columnAggregationFunctions: ColumnAggregationFunctions = {};

        if (data.frame instanceof IAggregableListFrame && data.frame.isAggregationRowVisible()) {
            const hasRows = data.frame.currentDataGrid.dataRows.length !== 0;
            // Řádek bude obsažen v tisku seznamu pokud byla zvolena alespoň jedna agregační funkce a zobrazuje se alespoň jeden záznam.
            if (data.frame.hasSelectedAggregationFunctions() && hasRows) {
                printAggregationRow = true;
                columnAggregationFunctions = data.frame.getColumnsAggregationFunctions();
            }
        }

        data.frame.currentDataGrid.columns.forEach((column) => {
            if (column.isVisible()) {
                const { columnId } = column;
                const aggregFunction: AggregationFunction | undefined | null = columnAggregationFunctions[columnId];
                columns.push({
                    attributeName: (column instanceof LovColumn) ? `${column.attrName}.${column.showAttrs[0]}` : column.attrName, // `
                    locKey: column.locKey,
                    locParams: column.locParams,
                    width: column.width,
                    aggregFunction,
                });
            }
        });

        let orderByContainsId = false;

        const orderBy = data.frame.orderBy.map((item) => {
            if (!orderByContainsId && item.attributeName.includes("ID")) orderByContainsId = true;
            return item.order === OrderType.ascending ? item.attributeName : `DESC ${item.attributeName}`;
        });

        if (!orderByContainsId) orderBy.push("ID");

        let caption = "";
        let captionLocKey = "";

        if (data.frame.masterFrame) {
            captionLocKey = data.frame.locKey;
        } else if (data.frame.ownerForm?.customTitle) {
            caption = data.frame.ownerForm?.customTitle;
        } else {
            captionLocKey = data.frame.ownerForm?.titleLocKey;
        }

        return {
            exportType: type,
            objectName: data.frame.objectName,
            formCaption: caption,
            formCaptionLocKey: captionLocKey,
            condition: data.frame.currentDataGrid.dataSource.condition,
            conditionParams: data.frame.currentDataGrid.dataSource.conditionParams,
            masterInstanceID: data.frame.masterFrame?.actualObject?.instanceID,
            readVersioned: data.readVersioned,
            orderBy: orderBy,
            attributes: columns,
            showAggregRowStr: printAggregationRow ? SHOW_AGGREGATION_ROW_STRING : undefined,
        };
    }

    /**
     * Vytvoří akce pro tisk a vloží je do menu seznamu
     * @param data Data, potřebná k vygenerování struktury pro tisk/export
     * @param requestContext Viz {@link RequestContext}
     */
    public static createExportActions(data: IExportData, requestContext?: RequestContext): void {
        const exportMenu = NodeAction.createDefault();
        exportMenu.toMenu = MenuType.mtPrint;
        exportMenu.icon = IconCode.export;
        exportMenu.locKey = "GUI-669366";//Export
        exportMenu.hasCaption = false;

        let action = FrameSharedMethods.createStandardAction(ActionType.standardExportCSV, async () => {
            if (await data.frame.ownerForm.checkAndSaveForm(this.convertRequestContextToSavingParams(requestContext))) {
                ExportManager.instance.exportFrame(FrameSharedMethods._getExportParams(data, SupportedExportFormats.CSV), requestContext);
            }
        }, data.frame);

        if (action != null) {
            action.enabled = true;
            action.visible = true;

            exportMenu.addAction(action);
        }

        action = FrameSharedMethods.createStandardAction(ActionType.standardExportExcel, async () => {
            if (await data.frame.ownerForm.checkAndSaveForm(this.convertRequestContextToSavingParams(requestContext))) {
                ExportManager.instance.exportFrame(FrameSharedMethods._getExportParams(data, SupportedExportFormats.Excel2007), requestContext);
            }
        }, data.frame);

        if (action != null) {
            action.enabled = true;
            action.visible = true;

            exportMenu.addAction(action);
        }

        data.frame.menu.addAction(exportMenu);
    }

    /**
     * Vytvoří akce pro export a vloží je do menu seznamu
     * @param data Data, potřebná k vygenerování struktury pro tisk/export
     * @param requestContext Viz {@link RequestContext}
     */
    public static createReportActions(data: IExportData, requestContext?: RequestContext): void {
        const reportMenu = NodeAction.createDefault();
        reportMenu.toMenu = MenuType.mtPrint;
        reportMenu.icon = IconCode.print;
        reportMenu.locKey = "GUI-668944"; //Tisk
        reportMenu.hasCaption = false;

        const action: IAction = FrameSharedMethods.createStandardAction(ActionType.standardPrintAll, async () => {
            if (await data.frame.ownerForm.checkAndSaveForm(this.convertRequestContextToSavingParams(requestContext))) {
                ExportManager.instance.printFrame(FrameSharedMethods._getReportParams(data), requestContext);
            }
        }, data.frame);

        if (action != null) {
            action.enabled = true;
            action.visible = true;

            reportMenu.addAction(action);
        }

        data.frame.menu.addAction(reportMenu);
    }

    /**
     * Vytvoří akci pro tisk záznamu
     * @param data Data, potřebná k vygenerování struktury pro tisk
     */
    public static createRecordReportAction(frame: IFrame, requestContext?: RequestContext): IAction {
        return FrameSharedMethods.createStandardAction(ActionType.standardPrintRecord, async () => {
            // V případě, že je vyvolán tisk z DetailFramu, tak je nutné kontrolovat, že jeho nadřízený frame má nastavený actualObject na null. Pokud nemá nadřízený frame zkontroluje se jeho actualObject.
            // Je to z toho důvodu, že se v metodě checkAndSaveForm volá při kliknutí na "Neukládat" metoda _cancelChangesOnFrames, která sice nasetuje na LF i DF actualObject na null,
            // ale vyvolání události actualObjectChanged není synchronní v rámci _setActualObject, takže po jejím doběhnutí ještě není vyvolaná událost
            // a tím pádem není v momentě kontroly nastavený na DetailFramu správný actualObject, ten se nastaví až potom co je vyvolán tisk pomocí printRecord

            const savingParams = this.convertRequestContextToSavingParams(requestContext);

            if (await frame.ownerForm.checkAndSaveForm(savingParams) && ((frame instanceof IDetailFrame && frame.masterFrame) ? frame.masterFrame.actualObject != null : frame.actualObject != null)) {
                ExportManager.instance.printRecord(frame.actualObject.id, frame.actualObject.objectName, frame.frameName, frame.ownerForm.formName, frame.ownerForm.readVersioned, convertContextFromFrameToRequest(frame.frameContext));
            }
        }, frame);
    }

    /**
     * Zobrazí graf
     * @param data Data, potřebná k vygenerování grafu
     * @param profileID id z userProfile
     */
    private static async _showChart(frame: IMasterableFrameWithDataGrid, profileID: string): Promise<void> {
        if (await frame.ownerForm.checkAndSaveForm()) {
            const paramdata: IFRMParams = {
                condition: "",
                isModal: true,
                frameRefreshType: 0,
                showDetail: true,
                openInEdit: false,
                openMode: 0,
                readVersioned: false,
                masterLinkToAttr: frame.currentDataGrid.objectName,
                executerFrame: frame, //je zapotrebi kvuli rozliseni jestli se jedna o graf otevirany z listframe nebo graf na formulari z xgenu
                ownerForm: frame.ownerForm,
                selectedID: profileID,
            };

            const formBuilder = DIContainer.instance.create(IChartFrameFormBuilder);
            const form = await formBuilder.build(paramdata);
            FormManager.instance.openFormModal({ form: form, type: ModalWindowType.parametricForm, showBreadcrumbNav: true, parametricData: { formContext: frame.ownerForm?.formContext } });
        }
    }

    /**
     * Nastaví parametry pro případné meziuložení záznamu a neuložení kontextu pro checkAndSaveForm
     * @param requestContext
     */
    public static convertRequestContextToSavingParams(requestContext: RequestContext): IFormSavingParameters {
        const hasEditContextId = requestContext?.getTransactionContextId()?.length > 0;

        return {
            skipSavingEditContextId: hasEditContextId,
            forceSave: hasEditContextId,
        };
    }

    /**
     * Vytvoří akce pro graf a vloží je do menu seznamu
     * @param frame Frame, pro který se akce vytváří
     */
    public static createGraphActions(frame: IMasterableFrameWithDataGrid): void {
        const action = FrameSharedMethods.createStandardAction(ActionType.standardGraph, async (cancellationToken: CancellationToken) => {
            const nodeActionGraphs = NodeAction.createDefault();
            nodeActionGraphs.icon = IconCode.graph;
            nodeActionGraphs.locKey = "GUI-680441"; // Grafy
            nodeActionGraphs.priority = 1;
            nodeActionGraphs.toMenu = MenuType.mtFrameMenu;

            const newGraph = new Action(async () => this._showChart(frame, null), "GUI-85936"); // Nový graf
            nodeActionGraphs.addAction(newGraph);

            // vytvořím akce pro uložené grafy
            const chartProfiles = await getDataReaderClient().getDataRowsWithParams("UserProfile", ["ID", "Data", "name"],
                ":UserProfile.ActualUserProfiles() AND DB.Upper(FormName) = DB.Upper(@0) AND DataType = @1",
                { params: [frame.ownerForm.formName, "ChartProfileMW"], dataLength: -1 }, undefined, convertContextFromFrameToRequest(frame.frameContext, cancellationToken));

            for (const chartProfile of chartProfiles) {
                const chartProfileJsonData = AttrValueReader.read(chartProfile["Data"]);
                const chartProfileParsedData: IChartProfileInfo = JSON.parse(chartProfileJsonData || null) || {};

                if (chartProfileParsedData.masterFrameName == frame.frameName) {
                    const graphAction = new ActionWithData(() => this._showChart(frame, AttrValueReader.read(chartProfile["ID"])));
                    graphAction.data = { chartName: chartProfileParsedData.chartName, chartProfileId: chartProfile["ID"] } as IGraphDataForAction;

                    nodeActionGraphs.addAction(graphAction);
                }
            }

            frame.menu.addAction(nodeActionGraphs);
        }, frame);

        action.toMenu = MenuType.mtFrameMenu;

        frame.menu.addAction(action);
    }

    /**
     * Vytvoří akci pro zobrazení informaci o počtu záznamů
     * @param frame Frame, pro který se akce vytváří
     * @param dataSource Datový zdroj
     */
    public static createRecordsCountAction(frame: IMasterableFrameWithDataGrid, dataSource: IDataSource): StandardAction {
        const action = FrameSharedMethods.createStandardAction(ActionType.standardRecordsCount, async () => {
            let msg: MessageWindow;
            try {
                UIManager.instance.showLoader();
                const info = await dataSource.count();
                msg = new MessageWindow(undefined, ["dialog-encryption"]);
                msg.header = "GUI-282888"; //Počet záznamů
                msg.contentBuilder = new RecordsInfoDialogBuilder(info);
                msg.detailButton = false;
            } finally {
                UIManager.instance.hideLoader();
            }
            msg?.show();
        }, frame);
        action.toMenu = MenuType.mtFrameMenu;
        return action;
    }

    /**
     * Vytvoří akci pro zobrazení včetně podřízených
     * @param frame Frame, pro který se akce vytváří
     */
    public static createShowSubRecordsAction(frame: IMasterableFrame): StandardTwoStateAction {
        const action = FrameSharedMethods.createStandardTwoStateAction(ActionType.standardShowSubRecords, async () => {
            action.checked = !action.checked;
            frame.showSubRecords = action.checked;

            if (frame.masterObject)
                await frame.refreshData(false);
        }, frame, false);

        action.checked = frame.showSubRecords;
        action.toMenu = MenuType.mtFrameMenu;
        return action;
    }

    /**
     * Rozparsuje statickou metodu na pár třída/metoda, případně použije třídu z objektu ownera
     * @param owner Owner pro případné zjištění třídy
     * @param method Metoda ke zparsování
     */
    public static parseStaticMethod(owner: IFrame | null, method: string): { objectName: string; methodName: string } {
        const position = method.indexOf(".");

        if (position > 0) {
            return { objectName: method.substr(0, position), methodName: method.substr(position + 1) };
        }

        if (!owner) {
            throw new InvalidOperationException(`Cannot parse static method ${method} without explicit objectName or owner frame.`);
        }

        return { objectName: owner.objectName, methodName: method };
    }

    /**
     * Metoda získá z daného elementu framu atribut OpenMode.
     * @param element HTMLElement framu.
     * @param enableInsert Značí, zdali povolit OpenModeType.insert. Výchozí hodnota je false.
     * @returns Vrací daný OpenModeType. Pokud není validní, vrací výchozí hodnotu OpenModeType.standard.
     */
    public static getOpenModeTypeFromAttribute(element: HTMLElement, enableInsert: boolean = false): OpenModeType {
        const openModeAttrValue = element.getAttribute("data-openmode");
        if (!openModeAttrValue) {
            return OpenModeType.standard;
        }
        const openModeType = EnumUtils.getEnumValueFromName(OpenModeType, openModeAttrValue.toLowerCase()) || OpenModeType.standard;
        if (openModeType === OpenModeType.insert && !enableInsert) {
            logger.warn(`${element.getAttribute("data-framename")} doesn't support attribute OpenMode='Insert'. OpenMode was set to default value.`);
            return OpenModeType.standard;
        } else {
            return openModeType;
        }
    }

    /**
     * Spustí callback ihned, pokud frame nemá mastera nebo jeho master už ji zinicializovaný.
     * Jinak callback spustí až po zinicializování master framu.
     * @param frame Frame, kterého se akce týká
     * @param action Callback vyvolaný po inicializaci
     */
    public static runWhenMasterInitialized<T>(frame: IFrame, action: () => T): T | void {
        const masterFrame = frame.masterFrame;
        if (!masterFrame || masterFrame.isInitialized) {
            return action();
        }

        masterFrame.initialized.add(() => action(), true);
    }

    /**
     * Spustí callback ihned, pokud je frame již zobrazený.
     * Jinak callback spustí až po prvním zobrazení framu.
     * @param frame Frame, kterého se akce týká
     * @param action Callback vyvolaný po prvním zobrazení
     */
    public static runWhenFirstShown<T>(frame: Control & IFrame, action: () => T): T | void {
        const afterMasterInitAction = () => this.runWhenMasterInitialized(frame, action);
        //frame může načítat data NEBO pokud na formuláři zobrazený jen detail, potřebujeme zinicializovat hlavní frame, který může blokovat detail ve své inicializaci
        if (frame.canValueLoad || (frame.isMainFrame && frame.ownerForm?.visibleOnlyDetail)) {
            return afterMasterInitAction();
        }

        const listener = (_: unknown, value: boolean) => {
            if (!value)
                return;

            afterMasterInitAction();
        };
        frame.canValueLoadChanged.add(listener, true);
    }

    /**
     * Metoda vytvoří na základě lokalizačního klíče caption element.
     * @param locKey Lokalizační klíč framu
     * @returns Vrací vytvořený element
     */
    public static createFrameCaptionElement(locKey: string): HTMLElement {
        const captionElement = createElement({ type: "span", classes: "frame-caption" });
        Localization.addLocData(captionElement, locKey);
        return captionElement;
    }

    /**
     * Najde hlavní nejvyšší masterframe v hiearchii
     * @param frame aktuální frame.
     */
    public static findMainFrame(frame: IMasterableFrame): IMasterableFrame | undefined {
        return frame?.masterFrame ? this.findMainFrame(frame.masterFrame) : frame;
    }

    /**
     * Vrací defaultní práva pro Insert, Edit a Delete pro daný form
     * @param form
     */
    public static getDefaultIEDRights(form: IForm, activeRoleIds: readonly string[]): StandardOperationRights {
        if (form == null) {
            return new StandardOperationRights(activeRoleIds);
        }

        if (form.viewMode || form.readVersioned) {
            return new StandardOperationRights(activeRoleIds, false, false, false, true);
        }

        if (!(form instanceof IForm)) {
            return new StandardOperationRights(activeRoleIds, true, true, true);
        }

        return new StandardOperationRights(activeRoleIds);
    }

    /**
     * Vrací objekt, který obsahuje informace o prvku
     * @param frame Aktuální frame
     * @param childInfo Částečné informace o prvku
     * @param clickedControl Komponenta, na kterou bylo kliknuto
     */
    public static async getControlInfo(frame: IFrameWithRights, childInfo: Partial<IControlInfoObject>, clickedControl: IGenericControl): Promise<Partial<IControlInfoObject>> {
        let attrName = childInfo.attributeName;
        if (frame instanceof ICompositesControl && attrName && attrName.includes(".") && !attrName.includes("|")) {
            if (frame?.composites) {
                const composites = frame.composites?.split(";") ?? [];
                const [ attrClass, attribute ] = attrName.split(".");
                const realClass = (composites.find(composite => composite.toLowerCase().includes(`${attrClass.toLowerCase()}=`)) ?? "=").split("=")[1];
                if (realClass !== "") {
                    attrName = `${attrClass}|${realClass}.${attribute}`;
                }
            }
        }

        if (!childInfo.additionalInfo) // Nebylo kliknuto na podporovanou podřízenou komponentu, nemá smysl pokračovat
            return childInfo;
        let attrInfo: IPublicAttributeInfo;
        if (attrName) {
            attrInfo = await ModelDescriptionsManager.instance.getAttributeInfo(frame.objectName, attrName);
        }
        if (clickedControl) {
            childInfo.additionalInfo.rights = frame.rightsManager.findRightsCollectionForControl(node => node?.component?.control && node.element.control === clickedControl);
            childInfo.additionalInfo.frameContext = clickedControl instanceof IHasFrameContext ? clickedControl.frameContext : frame.frameContext;
        }
        return {
            className: frame.objectName,
            dataType: attrInfo?.type,
            additionalTypeName: attrInfo?.additionalTypeName,
            ...childInfo,
        };
    }

    /** Namapuje ke každé definici sloupce příslušné informace o atributu.
     * @param className Název třídy, ke které atributy patří.
     * @param columnDefinitions Definice sloupců seznamu.
    */
    public static async mapColumnDefinitionsToAttributeInfos(className: string, columnDefinitions: IColumnDefinition[]): Promise<{ columnDefinition: IColumnDefinition; attributeInfo: IPublicAttributeInfo }[]> {
        const attrInfos = await ModelDescriptionsManager.instance.getAttributesInfo(className, columnDefinitions.map(columnDefinition => columnDefinition.attrName));

        return columnDefinitions.map(columnDefinition => {
            return { columnDefinition, attributeInfo: attrInfos.find(attrInfo => StringUtils.compareCaseInsensitive(attrInfo.attributeName, columnDefinition.attrName)) };
        });
    }

    /** Vygeneruje univerzální ID
     * @param isDebug pokud je nastaveno na true, tak generované id začíná prefixem podle `name`
     * @param name volitelné jméno, které je použito jako prefix, pokud je nastaven debug mode.
     */
    public static generateUuid(isDebug: boolean, name?: string): string {
        const guid = UUID.generate();
        return isDebug && name ? `${name}_${guid}` : guid;
    }
}

export interface IExtendedOperationsParams {
    /**
     * form na kterém leží MasterableFrame
     */
    ownerForm: IForm;
    /**
     * MasterableFrame
     */
    masterFrame: IMasterableFrame;
    /**
     * ExtendedOperation k rozparsování
     */
    afterGuiCreateMethod: string;
    /**
     * ExtendedOperation k rozparsování
     */
    afterGuiCreateFill: string;
    /**
     * Název vazby na nadřizeny objekt
     */
    masterLinkToAttr: string;
    /**
     * Instance ID nadrzeneho objektu pro masterLinkToAttr
     */
    masterObjectInstanceIDForLinkToAttr?: string;
    /**
     * Název vazby z nadřizeného objektu
     */
    masterAttr: string;
    /**
     * Zda se jedná o hlavní frame
     */
    isMainFrame: boolean;
    /**
     * Příznak, zda se má masterObject zabalit do ObjectListu
     */
    isMasterObjectList?: boolean;
    /**
     * Příznak, zda je aktivováno zobrazení včetně podřízených
     */
    showSubrecordsActive?: boolean;
}

/**
 * Data, potřebná k vygenerování struktury pro tisk/export
 */
export interface IExportData {
    /** Frame, na kterém probíhá tisk (ListFrame, MSLOV, ...) */
    frame: IMasterableFrameWithDataGrid & IDetailMasterableFrame & IListFrame;
    /** Zda budou čteny verzovaná data */
    readVersioned: boolean;
}
